import { first, get } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import { ChildProps, MutationFn } from 'react-apollo';
import { convertCurrency } from '@reverbdotcom/commons/src/convert_currency';
import {
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
} from '@reverbdotcom/commons/src/offers/offer_operations';
import NegotiationSummary from './negotiation_summary';
import UpdateOfferFormFields from './update_offer_form_fields';
import { offerTotal } from '@reverbdotcom/commons/src/offers/offer_calculator';
import URLHelpers from '../shared/url_helpers';
import * as elog from '@reverbdotcom/commons/src/elog';

import bind from '@reverbdotcom/commons/src/bind';

import {
  CommonsOffersEditOfferForm,
  CommonsOffersUpdateOfferForm,
  core_apimessages_Channel,
  Input_core_apimessages_UpdateNegotiationRequest,
  Input_core_apimessages_OfferItemRequest,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
} from '../../schemas/rql';

import {
  IUserContext,
  withUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';
import Modal from '@reverbdotcom/commons/src/components/modal';
import { OfferListingSidebar } from '@reverbdotcom/commons/src/offers/offer_listing_sidebar';

import {
  lastOffer,
  lastOfferPrices,
  lastOfferPricesFor,
  activeNegotiation,
  offerShippingPriceAmount,
  offerQuantity,
  userCurrency,
  originalCurrency,
} from './negotiation_getters';
import {
  UserInputOffer,
  IOfferPrices,
} from '@reverbdotcom/commons/src/offers/negotiation_types';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { connect } from '@reverbdotcom/commons/src/connect';

interface IExternalProps {
  listingId: string;
  isSeller: boolean;
  negotiationId: string;
  shopChannel: core_apimessages_Channel;
  onSuccess: (requestingParty: NegotiationParty) => void;
  isDialogOpen?: boolean;
  onRequestDialogClose?: Function;
  negotiationAction: NegotiationAction;
}

interface IState {
  error: string;
  isFormSaving: boolean;
  offer: UserInputOffer;
}

interface ApolloProps {
  updateNegotiation: MutationFn<CommonsOffersUpdateOfferForm.Negotiation, CommonsOffersUpdateOfferForm.Variables>;
}

type IProps = ChildProps<IExternalProps, CommonsOffersEditOfferForm.Query> & ApolloProps & IUserContext;

const TRACKING_COMPONENT = 'UpdateOfferModal';

interface BaseOffer {
  price: string;
  shippingPrice: string;
  quantity: number;
  message: string;
}

export class UpdateOfferModal extends React.Component<IProps, IState> {
  state: IState = {
    error: '',
    isFormSaving: false,
    offer: {
      price: '',
      message: '',
      quantity: null,
    },
  };

  get trackingContext() {
    return {
      listingId: this.props.listingId,
      negotiationId: this.props.negotiationId,
      shopCurrency: this.props.data.listing.shop.currency,
      userCurrency: this.userCurrency,
    };
  }

  get isLoading() {
    return get(this.props, 'data.loading');
  }

  get isInitializing() {
    const listingIsNotLoaded = !get(this.props, 'data.listing');

    return this.isLoading && listingIsNotLoaded;
  }

  get offerError() {
    return get(this.props, 'data.error.message');
  }

  get inputOffer(): BaseOffer {
    const shippingPrice = offerShippingPriceAmount(
      this.state.offer.shippingPrice,
      get(this.props.data, 'me'),
    );
    const quantity = offerQuantity(this.lastOffer, this.state.offer.quantity);

    return {
      quantity,
      shippingPrice,
      price: this.state.offer.price,
      message: this.state.offer.message,
    };
  }

  get existingOffer(): BaseOffer {
    return {
      price: this.lastOffer.prices.price.display.amount,
      shippingPrice: this.lastOffer.prices.shippingPrice.display.amount,
      quantity: first(this.lastOffer.offerItems).quantity,
      message: this.state.offer.message,
    };
  }

  get offerToSubmitOnUpdate(): Input_core_apimessages_UpdateNegotiationRequest {
    const baseOffer = this.isCounterOffer ? this.inputOffer : this.existingOffer;

    const shopCurrencyOfferPrice = convertCurrency(
      this.userCurrency,
      originalCurrency(this.props.data),
      this.exchangeRates,
      baseOffer.price,
    );
    const shopCurrencyOfferShippingPrice = convertCurrency(
      this.userCurrency,
      originalCurrency(this.props.data),
      this.exchangeRates,
      baseOffer.shippingPrice,
    );

    return {
      action: this.props.negotiationAction,
      id: this.negotiation.id,
      message: this.state.offer.message,
      offerItems: [
        {
          listingId: this.props.data.listing.id,
          quantity: baseOffer.quantity,
          price: shopCurrencyOfferPrice,
          shippingPrice: shopCurrencyOfferShippingPrice,
        },
      ],
    };
  }

  get negotiation() {
    return activeNegotiation(get(this.props.data, 'me'));
  }

  get lastOffer() {
    return lastOffer(this.negotiation);
  }

  get isCounterOffer() {
    return this.props.negotiationAction === NegotiationAction.COUNTER;
  }

  get party(): NegotiationParty {
    return this.props.isSeller ? NegotiationParty.SELLER : NegotiationParty.BUYER;
  }

  get isBuyer() {
    return this.party === NegotiationParty.BUYER;
  }

  get exchangeRates() {
    return get(this.props, 'data.exchangeRates.rates', []);
  }

  @bind
  async updateNegotiation(): Promise<Input_core_apimessages_OfferItemRequest> {
    const input = this.offerToSubmitOnUpdate;
    await this.props.updateNegotiation({ variables: { input } });
    return first(input.offerItems);
  }

  notifySuccess() {
    this.props.onSuccess(this.party);
  }

  @bind
  closeDialog() {
    if (this.props.onRequestDialogClose) {
      this.props.onRequestDialogClose();
    }
  }

  @bind
  async handleSubmit(e) {
    e.preventDefault();
    try {
      this.setState({ isFormSaving: true, error: null });
      await this.updateNegotiation();
      this.setState({ isFormSaving: false });

      this.notifySuccess();
      this.closeDialog();
    } catch (errors) {
      this.setState({ error: I18n.t('discovery.offers.updateOfferSubmitError'), isFormSaving: false });

      const { message: graphQLErrorMessage } = first(get(errors, 'graphQLErrors', [{ message: '' }]));
      elog.error(
        TRACKING_COMPONENT,
        {
          ...this.trackingContext,
          errors,
        },
        new Error(graphQLErrorMessage),
      );
    }
  }

  @bind
  handleOfferChange(offer: Partial<UserInputOffer>) {
    this.setState({
      offer: {
        ...this.state.offer,
        ...offer,
      },
    });
  }

  renderOfferSidebar() {
    const listing = get(this.props, 'data.listing');

    return (
      <OfferListingSidebar
        listing={listing}
        isLoading={this.isLoading}
        isSeller={this.props.isSeller}
        listingUrlGenerator={URLHelpers.listingPagePath}
      />
    );
  }

  showError() {
    const noNegotiationFound = (!this.negotiation && !this.isLoading);
    const missingExchangeRates = !this.props.data.exchangeRates &&
      !this.props.isSeller &&
      (this.userCurrency !== originalCurrency(this.props.data));

    return get(this.props, 'data.error') || noNegotiationFound || missingExchangeRates;
  }

  renderOffersForm() {
    if (this.showError()) {
      return (
        <RCAlertBox type="error">
          <p className="mb-0">
            {I18n.t('discovery.offers.error')}
          </p>
        </RCAlertBox>
      );
    }

    if (this.isInitializing) {
      return (
        <div className="offers-form offers-form--loading">
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
          <div className="offers-form-placeholder" />
        </div>
      );
    }

    return (
      <form
        className="offers-form"
        onSubmit={this.handleSubmit}
      >
        <div className="offers-form__header">
          <NegotiationSummary
            prices={this.lastOfferPrices}
            originalPrices={this.originalLastOfferPrices}
            listing={this.props.data.listing}
            negotiation={this.negotiation}
            party={this.party}
          />
        </div>
        <UpdateOfferFormFields
          error={this.state.error}
          listing={this.props.data.listing}
          negotiation={this.negotiation}
          currency={this.userCurrency}
          exchangeRates={this.exchangeRates}
          offer={this.inputOffer}
          onOfferChange={this.handleOfferChange}
          action={this.props.negotiationAction}
          party={this.party}
        />
        <div className="offers-form__actions">
          <button
            className="offers-form__actions__save"
            type="submit"
            disabled={this.disableSaveButton}
          >
            {this.saveButtonText}
          </button>
          <button
            className="offers-form__actions__back"
            onClick={this.closeDialog}
            type="button"
          >
            {I18n.t('commons.offers.backAction')}
          </button>
        </div>
      </form>
    );
  }

  get modalHeaderTitle() {
    const action = this.props.negotiationAction.toLocaleLowerCase();
    return I18n.t(`discovery.offers.headerTitle.${action}`);
  }

  get disableSaveButton() {
    return this.state.isFormSaving || (this.isCounterOffer && !this.state.offer.price);
  }

  get counterOfferSubmitButtonText() {
    if (!this.state.offer.price) {
      return I18n.t('discovery.offers.submitButton.counter.loading');
    }

    return I18n.t('discovery.offers.submitButton.counter.counterOfferForAmount', {
      offerTotal: offerTotal(this.inputOffer, this.userCurrency).display,
    });
  }

  get saveButtonText() {
    switch (this.props.negotiationAction) {
      case NegotiationAction.ACCEPT:
        return I18n.t('discovery.offers.headerTitle.accept');
      case NegotiationAction.REJECT:
        return I18n.t('discovery.offers.headerTitle.reject');
      case NegotiationAction.COUNTER:
      default:
        return this.counterOfferSubmitButtonText;
    }
  }

  get userCurrency() {
    return userCurrency(this.props.data, this.props.user);
  }

  get lastOfferPrices(): IOfferPrices {
    return lastOfferPrices(get(this.props.data, 'me'));
  }

  get originalLastOfferPrices(): IOfferPrices {
    return lastOfferPricesFor(NegotiationParty.SELLER, this.negotiation);
  }

  render() {
    if (!this.props.data) {
      return null;
    }

    return (
      <Modal
        isOpen={this.props.isDialogOpen}
        onRequestClose={this.closeDialog}
        headerTitle={this.modalHeaderTitle}
        sidebarContent={this.renderOfferSidebar()}
        paddedBody
      >
        {this.renderOffersForm()}
      </Modal>
    );
  }
}

const connectOfferModal = connect<IExternalProps>([
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
])(UpdateOfferModal);

export default withUserContext<IExternalProps>(connectOfferModal);
